'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import getSearchClient from 'server/repository/algolia/client';
import { DictionaryService } from 'service/dictionary/dictionaryService';

import type { IBreadcrumbTitles } from 'components/Breadcrumbs';
import type { ISearchOverlay } from 'components/HeaderNavigation/components/MobileNavigation';
import type { ISocialMediaComponent } from 'components/SocialMedia/types';
import type { ICardItem } from 'components/Subcategory';
import type { AlgoliaExtension } from 'server/repository/hygraph/generated/graphqlTypes';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import type { IHeroCarouselProps } from 'types/LandingPageData';
import type { IMetaData } from 'types/MetaData';

import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import { buildAlgoliaQuery } from 'server/repository/algolia/builder/Builder';
import { TrackingService } from 'service/tracking/trackingService';
import TemplateMapper from 'templates/TemplateMapper';
import { RecipeGridProvider } from '@/components/RecipeGrid/RecipeGridProvider';
import { RecipeGridHeader } from '@/components/RecipeGrid/RecipeGridHeader';
import { RecipeGrid } from '@/components/RecipeGrid';
import { RecipeGridNavigation } from '@/components/RecipeGrid/RecipeGridNavigation';

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const IntroText = dynamic(() => import('components/IntroText').then((m) => m.IntroText), {
  ssr: true,
});

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;

const SliderCarousel = dynamic(
  () => import('components/SliderCarousel').then((m) => m.SliderCarousel),
  { ssr: true },
);

const SocialMedia = dynamic(() => import('components/SocialMedia').then((m) => m.SocialMedia), {
  ssr: true,
});

const Spacer = dynamic(() => import('components/Spacer').then((m) => m.Spacer), {
  ssr: true,
});

const Subcategory = dynamic(
  () => import('components/Subcategory').then((m: any) => m.Subcategory),
  { ssr: true },
) as any;

interface RecipeCategory {
  name: string;
  slug: string;
}

interface SubCategory {
  algoliaExtension: AlgoliaExtension;
  recipeSubcategory: any;
  slug: string;
  id: string;
  subcategoryPageName: string;
  recipeSubCategoryName: string;
  heroCarouselComponent: IHeroCarouselProps;
  metaData: IMetaData;
  introText: {
    id?: string;
    super?: string;
    title?: string;
    description?: string;
    titleTag?: string;
  };
  recipeCategory: {
    name: string;
    slug: string;
  };
  subSubCategorySuper: string;
  subSubCategoryTitle: string;
  recipeSubSubcategories: {
    recipeSubCategoryName: string;
    heroCarouselComponent: IHeroCarouselProps;
    metaData: IMetaData;
    introText: {
      id?: string;
      super?: string;
      title?: string;
      description?: string;
    };
    recipeCategory: {
      name: string;
      slug: string;
    };
  }[];
  productCategory?: {
    id: string;
    products?: {
      id: string;
      slug: string;
      productTitle: string;
      productImage: {
        url: string;
      };
    }[];
  };
  recipeIDs?: string[];
}

function ClientRecipesSubcategorySlugPage({
  subcategory,
  dynamicPageComponents,
  followUsData,
  query,
  recipeEnv,
  breadcrumbsCMSData,
  searchOverlayData,
  difficultyCopy,
  recipeCopy,
  recipeCategory,
  searchItemLabels,
  searchPageLabels,
  isSubSubCategory,
  host,
  locale,
}: {
  subcategory: SubCategory;
  dynamicPageComponents: any;
  followUsData: ISocialMediaComponent;
  recipeEnv: string;
  query: string;
  breadcrumbsCMSData: IBreadcrumbTitles;
  searchOverlayData: ISearchOverlay;
  recipeCategory: RecipeCategory;
  difficultyCopy: Record<string, string>;
  recipeCopy: DictionaryItem[];
  searchItemLabels: Record<string, string>;
  searchPageLabels: Record<string, string>;
  isSubSubCategory: boolean;
  host: string;
  locale: string;
}) {
  const { pathTranslations, userToken } = useGlobalContext();
  const watchVideoLabel = DictionaryService.getCopy('see_video', recipeCopy);
  const recipesAmountLabel = DictionaryService.getCopy('recipes', recipeCopy);
  const heroCarouselData = subcategory?.heroCarouselComponent;
  const introData = subcategory?.introText;
  const recipeSubSubcategories = subcategory?.recipeSubSubcategories;
  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const searchClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient,
      });
    }
  }, [searchOverlayData]);

  const subcategoryData = {
    subHeading: subcategory?.subSubCategorySuper || '',
    heading: subcategory?.subSubCategoryTitle || '',
  };

  const subcategoryCardsData = recipeSubSubcategories?.map((subSubCategory: any) => {
    const amount =
      subSubCategory?.algoliaExtension?.extension?.numberOfHits ||
      subSubCategory?.recipeIDs.length ||
      0;
    return {
      productsAmount: `${amount} ${recipesAmountLabel || 'recipes'}`,
      id: subSubCategory?.id,
      categoryId: subSubCategory?.id,
      category: '',
      images: {
        desktop: subSubCategory?.promoImage?.asset?.[0]?.desktop,
        mobile: subSubCategory?.promoImage?.asset?.[0]?.mobile,
      },
      title: subSubCategory?.recipeSubCategoryName,
      link: {
        url: `${pathTranslations.recipesSubCategory}${recipeCategory.slug}/${
          subcategory?.slug || ''
        }/${subSubCategory?.slug}`,
        displayName: '',
      },
    };
  });

  const breadcrumbsCategories = [
    {
      slug: pathTranslations.recipes,
      displayName: breadcrumbsCMSData?.recipesText?.value,
    },
  ];

  if (!isSubSubCategory) {
    breadcrumbsCategories.push({
      slug: `${pathTranslations.recipesCategory}${recipeCategory?.slug}`,
      displayName: recipeCategory?.name,
    });
  } else {
    breadcrumbsCategories.push({
      slug: `${pathTranslations.recipesCategory}${recipeCategory?.slug}`,
      displayName: recipeCategory?.name,
    });
    breadcrumbsCategories.push({
      slug: `${pathTranslations.recipesSubCategory}${recipeCategory?.slug}/${subcategory?.recipeSubcategory?.slug}`,
      displayName: subcategory?.recipeSubcategory?.recipeSubCategoryName || '',
    });
  }

  useEffect(() => {
    TrackingService.contentGroups({ cg1: 'recipe categories', cg2: recipeCategory?.name || '' });
    return () => {};
  }, []);

  return (
    <>
      <Breadcrumbs
        titles={breadcrumbsCMSData}
        pageName={subcategory?.recipeSubCategoryName}
        categories={breadcrumbsCategories}
      />
      {!heroCarouselData && <Spacer />}
      <div className="flex flex-col items-center mx-auto mb-40 relative">
        {heroCarouselData && (
          <>
            <SliderCarousel
              bannerMasterbrands={heroCarouselData?.bannerMasterbrands}
              banners={heroCarouselData?.banners}
              isForBanners
              type="carousel"
              watchVideoLabel={watchVideoLabel}
            />
            <Spacer />
          </>
        )}
        {introData && (
          <>
            <IntroText
              super={introData.super || ''}
              title={introData.title || ''}
              description={introData.description || ''}
              titleTag={introData.titleTag ?? ''}
            />
            <Spacer />
          </>
        )}
        {!!(recipeSubSubcategories?.length > 0) && (
          <Subcategory
            titles={subcategoryData}
            cardData={subcategoryCardsData as ICardItem[]}
            subcategoryCardVariation="object"
          />
        )}
      </div>
      <Grid>
        <GridRow>
          <div className="mb-5">
            <RecipeGridProvider>
              <RecipeGridHeader searchItemLabels={searchItemLabels} />
              <RecipeGrid
                userToken={userToken}
                host={host}
                pathTranslations={pathTranslations}
                searchPageLabel={searchPageLabels?.see_recipe}
                recipeEnv={recipeEnv}
                locale={locale}
                filters={buildAlgoliaQuery(
                  subcategory?.algoliaExtension?.extension?.refinementList,
                )}
                query={query}
                categoryName={subcategory?.recipeSubCategoryName}
                difficultyCopy={difficultyCopy}
                recipeIDs={subcategory?.recipeIDs}
                facets={[
                  'menu_types',
                  'preparation_time',
                  'servings',
                  'difficulty',
                  'diet_types',
                  'seasons.title',
                  'occasions',
                  'rating_effective',
                  'preparation_types',
                  'keywords',
                ]}
              />
              <RecipeGridNavigation loadMoreLabel={searchPageLabels?.load_more} />
            </RecipeGridProvider>
          </div>
        </GridRow>
      </Grid>
      {dynamicPageComponents && (
        <TemplateMapper
          components={dynamicPageComponents}
          copy={[]}
          recipeCopy={recipeCopy}
          difficultyCopy={difficultyCopy}
          searchItemLabels={searchItemLabels}
          searchData={searchOverlayDataExtended}
        />
      )}
      {followUsData && (
        <SocialMedia
          id={followUsData.id}
          super={followUsData.super}
          title={followUsData.title}
          socialMedia={followUsData.socialMedia}
          middlegroundImage={followUsData.middlegroundImage}
          foregroundImage={followUsData.foregroundImage}
        />
      )}
    </>
  );
}

export default ClientRecipesSubcategorySlugPage;
